import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { RackMap } from '@/components/admin/rack-map';
import { Badge, Callout, Field, buttonClass, inputClass } from '@/components/ui';
import { adminLocationsQuery, adminShellQuery } from '@/queries';
import { looksLikeRackCode, RACK_CODE_REFUSAL } from '@/lib/location-code';
import type { Location } from '@/lib/stock-types';

/**
 * Locations. Spec 003 US-2, task T056.
 *
 * This is also where a stocktake begins, because a count is of a place. The
 * stocktake screen lists counts and could not start one: the route existed with
 * nothing calling it, which is a fix reported and never wired (R-45).
 *
 * A locked shelf is shown as locked, with what that means, because the first
 * time somebody cannot pick from a bay they will want to know why rather than
 * assume a fault.
 */

async function call(path: string, init?: RequestInit) {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  return res.status === 204 ? null : await res.json();
}

/**
 * What a row in the list can ask the page to do. Spec 007.
 *
 * A remove hides the row it happened on, so the "Removed. Undo" notice cannot
 * live in that row: it would vanish with it. The page shows it instead. The rack
 * map asks the page to open the racking form already filled in.
 */
export interface RackPreset {
  key: number;
  siteId: string;
  row: string;
  bayFrom: string; bayTo: string;
  shelfFrom: string; shelfTo: string;
  tubFrom: string; tubTo: string;
  subFrom: string; subTo: string;
  title: string;
}

const LocationsUi = createContext<{
  removed: (loc: { id: string; code: string }) => void;
  addRack: (preset: Omit<RackPreset, 'key'>) => void;
}>({ removed: () => {}, addRack: () => {} });

/** Bring back a removed location. Same id, same code, same history. */
function useRestore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call(`/api/locations/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ active: true }),
    }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'locations'] }),
  });
}

function Zone({ zone, locations }: { zone: string; locations: Location[] }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: (locationId: string) =>
      call('/api/stock-takes', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ location_id: locationId }),
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'locations'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'stock-takes'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <section className="mb-6">
      <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-muted">
        {zone}
      </h2>

      {error && (
        <p className="mb-3 rounded-[10px] border border-line bg-panel p-3 text-[14px]">
          {error}
        </p>
      )}

      <div className="rounded-[10px] border border-line bg-panel">
        {locations.map((loc) => (
          <div
            key={loc.id}
            className="flex flex-wrap items-center justify-between gap-3 border-b
                       border-line px-4 py-3 last:border-0"
          >
            <span className="flex min-w-[240px] flex-col">
              <span className="flex items-center gap-2 font-semibold">
                {loc.code}
                {!loc.active && <Badge tone="warn">removed</Badge>}
                {!loc.pickable && <Badge tone="warn">not pickable</Badge>}
                {loc.locked_by_take_id && <Badge tone="warn">being counted</Badge>}
              </span>
              <span className="text-[13px] text-muted">
                {loc.label}
                {/* Shelf and tub are added only for the old style of location.
                    A rack location has a row, and its label already says
                    "shelf A, tub 01", so adding them again printed each one
                    twice. */}
                {!loc.row_code && loc.shelf && ` · shelf ${loc.shelf}`}
                {!loc.row_code && loc.tub && ` · tub ${loc.tub}`}
              </span>
            </span>

            <span className="text-[14px] text-muted">
              {loc.lines === 0
                ? 'empty'
                : `${loc.lines} ${loc.lines === 1 ? 'item' : 'items'}, ${loc.units} units`}
            </span>

            {/* The label opens in a tab and prints from there. 62mm x 29mm,
                with the code as a Code 128 barcode a handheld reads. The size
                is a default until Q-03 says which printer the unit has. T052. */}
            <a
              href={`/api/locations/${loc.id}/label`}
              target="_blank"
              rel="noreferrer"
              className="rounded-[10px] border border-line px-3 py-2 text-[14px]"
              aria-label={`Print the label for ${loc.code}`}
            >
              Print label
            </a>

            {!loc.active ? (
              <RestoreButton id={loc.id} code={loc.code} />
            ) : loc.locked_by_take_id ? (
              <Link
                to="/admin/stock-takes"
                className="rounded-[10px] border border-line px-3 py-2 text-[14px]"
              >
                Go to the count
              </Link>
            ) : (
              <button
                type="button"
                disabled={open.isPending}
                onClick={() => open.mutate(loc.id)}
                className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold disabled:opacity-50"
              >
                Start a count
              </button>
            )}

            {/* Tracker #2. There was an Add and nothing else, so a shelf
                created with a typo stayed that way for ever. */}
            {loc.active && <EditLocation loc={loc} />}
          </div>
        ))}
      </div>
    </section>
  );
}


/**
 * CHANGING A SHELF, AND TAKING ONE OUT OF USE. Tracker #2, FR-509.
 *
 * Guy: "I feel that I can not remove stock Locations or edit only Add, Can you
 * please advise." He was right. The screen could create a location and nothing
 * else, so a shelf added with a typo stayed that way.
 *
 * THE CODE IS NOT EDITABLE, and that is deliberate rather than unfinished. The
 * code is printed on a label stuck to a physical shelf. Changing it here would
 * send everybody to the wrong place while the shelf still says the old thing.
 * Renaming a place means printing a new label, which means making a new
 * location and moving the stock onto it. The name, whether stock can be picked
 * from it, and whether it is in use are the three that can change safely.
 */
function RestoreButton({ id, code }: { id: string; code: string }) {
  const restore = useRestore();
  return (
    <span className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={restore.isPending}
        onClick={() => restore.mutate(id)}
        className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold disabled:opacity-50"
        aria-label={`Restore ${code}`}
      >
        {restore.isPending ? 'Restoring' : 'Restore'}
      </button>
      {restore.isError && (
        <span className="text-[12px] text-[color:var(--color-danger-ink)]">
          {(restore.error as Error).message}
        </span>
      )}
    </span>
  );
}

function EditLocation({ loc }: { loc: Location }) {
  const qc = useQueryClient();
  const ui = useContext(LocationsUi);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(loc.label ?? '');
  const [pickable, setPickable] = useState(loc.pickable);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  const done = () => {
    setError(null);
    void qc.invalidateQueries({ queryKey: ['admin', 'locations'] });
  };

  const save = useMutation({
    mutationFn: () => call(`/api/locations/${loc.id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ label, pickable }),
    }),
    onSuccess: () => { done(); setOpen(false); },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: () => call(`/api/locations/${loc.id}`, { method: 'DELETE' }) as Promise<{
      outcome: string; detail: string;
    }>,
    onSuccess: (r) => {
      done();
      setConfirming(false);
      setSaid(r.detail);
      // The row is hidden now, so the Undo lives on the page. Spec 007 FR-706.
      ui.removed({ id: loc.id, code: loc.code });
    },
    onError: (e: Error) => { setConfirming(false); setError(e.message); },
  });

  if (!open) {
    return (
      <div className="flex flex-col items-end gap-1">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-[10px] border border-line px-3 py-2 text-[14px]"
        >
          Edit
        </button>
        {said && <span className="max-w-[320px] text-right text-[12px] text-muted">{said}</span>}
      </div>
    );
  }

  return (
    <div className="w-full rounded-[10px] border border-line bg-raised p-3">
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <Field label="Name" htmlFor={`loc-label-${loc.id}`} hint="What somebody standing there calls it">
          <input
            id={`loc-label-${loc.id}`}
            className={inputClass}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </Field>
        <label className="flex items-center gap-2 self-end pb-2 text-[13px]">
          <input
            type="checkbox"
            checked={pickable}
            onChange={(e) => setPickable(e.target.checked)}
          />
          Stock can be picked from here
        </label>
      </div>

      <p className="mt-2 text-[12px] text-muted">
        The code <span className="font-[family-name:var(--font-mono)]">{loc.code}</span>{' '}
        cannot be changed, because it is printed on the shelf. To rename a place,
        add a new one and move the stock onto it.
      </p>

      {error && <Callout tone="danger" title={error} className="mt-3" />}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={buttonClass('primary', 'sm')}
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving' : 'Save'}
        </button>
        <button
          type="button"
          className={buttonClass('secondary', 'sm')}
          onClick={() => { setOpen(false); setError(null); }}
        >
          Cancel
        </button>

        <span className="flex-1" />

        {/*
          * Removing is a tertiary control on the far side, and it asks first.
          * Making a destructive action visually loud makes it easier to hit by
          * accident, which is the opposite of what it needs.
          */}
        {confirming ? (
          <span className="flex items-center gap-2">
            <span className="text-[12.5px]">Remove this shelf?</span>
            <button
              type="button"
              className={buttonClass('secondary', 'sm')}
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? 'Removing' : 'Yes, remove'}
            </button>
            <button
              type="button"
              className="text-[12.5px] text-muted underline"
              onClick={() => setConfirming(false)}
            >
              No
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="text-[12.5px] text-[color:var(--color-danger-ink)] underline"
            onClick={() => setConfirming(true)}
          >
            Remove this shelf
          </button>
        )}
      </div>

      <p className="mt-2 text-[12px] text-muted">
        A removed location is hidden, not deleted. You can restore it, or add it
        again with the racking form, and it comes back with the same code.
      </p>
    </div>
  );
}

/**
 * A WHOLE ROW OF RACKING, IN ONE ACTION. Spec 005 FR-507.
 *
 * Christian, more than once: "where is the location info for each stock or
 * inventory? I have been asking this for a few times now."
 *
 * The column has always been on the stock screen. What it showed was
 * `OBSESSED-GYMWEAR`, because that is the only location that existed: a Shopify
 * fulfilment record that arrived with the catalogue, not a place. Every one of
 * the 8,899 units was in it.
 *
 * The six part code exists in the database now, and this is the missing link.
 * Guy's store room is rows of shelving with labelled tubs, which is "row A,
 * bays 1 to 12, shelves A to D": 48 locations. Adding them one form at a time
 * is how this gets abandoned half way through, and a store room labelled half
 * way is worse than one not labelled at all, because the codes that do exist
 * look authoritative while the stock is still not where the system says.
 */
function AddRack({ preset }: { preset?: RackPreset | null }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [siteId, setSiteId] = useState('');
  const [row, setRow] = useState('');
  const [bayFrom, setBayFrom] = useState('1');
  const [bayTo, setBayTo] = useState('12');
  const [shelfFrom, setShelfFrom] = useState('A');
  const [shelfTo, setShelfTo] = useState('D');
  const [tubFrom, setTubFrom] = useState('');
  const [tubTo, setTubTo] = useState('');
  // The compartment in the tub. The sixth part, and it was missing from this
  // form while the database had it all along.
  const [subFrom, setSubFrom] = useState('');
  const [subTo, setSubTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ created: number; restored: number; already: number } | null>(null);

  // The rack map's "Add a shelf to this bay" and "Add a tub to this shelf"
  // open this form with everything filled in. Spec 007 FR-703.
  useEffect(() => {
    if (!preset) return;
    setSiteId(preset.siteId); setRow(preset.row);
    setBayFrom(preset.bayFrom); setBayTo(preset.bayTo);
    setShelfFrom(preset.shelfFrom); setShelfTo(preset.shelfTo);
    setTubFrom(preset.tubFrom); setTubTo(preset.tubTo);
    setSubFrom(preset.subFrom); setSubTo(preset.subTo);
    setTitle(preset.title); setMade(null); setError(null); setOpen(true);
    requestAnimationFrame(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [preset]);

  const sites = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: async () => {
      const res = await fetch('/api/sites', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Could not read the buildings.');
      return (await res.json()) as { sites: Array<{ id: string; code: string; name: string }> };
    },
    enabled: open,
  });

  // Default to the first REAL building. Site 00 is "received, not yet racked",
  // which is where stock starts, not somewhere you build shelves.
  const choices = (sites.data?.sites ?? []).filter((x) => x.code !== '00');
  const chosen = siteId || choices[0]?.id || '';

  const n = (v: string) => (v.trim() === '' ? null : Number(v));

  /** What this will make, said before it is made. */
  const count = (() => {
    const bays = n(bayFrom) != null && n(bayTo) != null
      ? Math.max(0, (n(bayTo) as number) - (n(bayFrom) as number) + 1) : 0;
    const shelves = shelfFrom && shelfTo
      ? Math.max(0, shelfTo.toUpperCase().charCodeAt(0) - shelfFrom.toUpperCase().charCodeAt(0) + 1)
      : 0;
    const tubs = n(tubFrom) != null && n(tubTo) != null
      ? Math.max(0, (n(tubTo) as number) - (n(tubFrom) as number) + 1) : 0;
    const subs = subFrom && subTo
      ? Math.max(0, subTo.toUpperCase().charCodeAt(0) - subFrom.toUpperCase().charCodeAt(0) + 1)
      : 0;
    if (bays === 0) return 1;
    if (shelves === 0) return bays;
    if (tubs === 0) return bays * shelves;
    if (subs === 0) return bays * shelves * tubs;
    return bays * shelves * tubs * subs;
  })();

  const add = useMutation({
    mutationFn: () =>
      call('/api/locations/rack', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          site_id: chosen,
          row,
          bay_from: n(bayFrom), bay_to: n(bayTo),
          shelf_from: shelfFrom || null, shelf_to: shelfTo || null,
          tub_from: n(tubFrom), tub_to: n(tubTo),
          sub_from: subFrom || null, sub_to: subTo || null,
        }),
      }) as Promise<{ created: number; restored?: number; already?: number }>,
    onSuccess: (r) => {
      setError(null);
      setMade({ created: r.created, restored: r.restored ?? 0, already: r.already ?? 0 });
      setRow('');
      void qc.invalidateQueries({ queryKey: ['admin', 'locations'] });
    },
    onError: (e: Error) => { setMade(null); setError(e.message); },
  });

  if (!open) {
    return (
      <button type="button" className={buttonClass('primary', 'sm')} onClick={() => setOpen(true)}>
        Add a row of racking
      </button>
    );
  }

  return (
    <form
      ref={formRef}
      className="w-full rounded-[10px] border border-line bg-panel p-4"
      onSubmit={(e) => { e.preventDefault(); add.mutate(); }}
    >
      {title && <p className="mb-2 text-[14px] font-semibold">{title}</p>}
      <p className="mb-3 text-[12.5px] text-muted">
        A code reads from the largest thing to the smallest:{' '}
        <span className="font-[family-name:var(--font-mono)]">01-A-01-A-01-A</span>{' '}
        is building 01, row A, bay 01, shelf A, tub 01, compartment A. Leave a
        box empty to stop there.
      </p>

      <div className="grid gap-3 sm:grid-cols-[1fr_90px]">
        <Field label="Building" htmlFor="rack-site">
          <select
            id="rack-site" className={inputClass} value={chosen}
            onChange={(e) => setSiteId(e.target.value)}
          >
            {choices.map((x) => (
              <option key={x.id} value={x.id}>{x.code} &middot; {x.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Row" htmlFor="rack-row" hint="A letter">
          <input
            id="rack-row" className={inputClass} value={row} required
            onChange={(e) => setRow(e.target.value.toUpperCase())} placeholder="A"
          />
        </Field>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Bays" htmlFor="rack-bay-from" hint="From and to">
          <div className="flex items-center gap-2">
            <input
              id="rack-bay-from" className={inputClass} value={bayFrom} inputMode="numeric"
              onChange={(e) => setBayFrom(e.target.value)}
            />
            <span className="text-[12px] text-muted">to</span>
            <input
              className={inputClass} value={bayTo} inputMode="numeric"
              aria-label="Last bay"
              onChange={(e) => setBayTo(e.target.value)}
            />
          </div>
        </Field>
        <Field label="Shelves" htmlFor="rack-shelf-from" hint="Letters, in each bay">
          <div className="flex items-center gap-2">
            <input
              id="rack-shelf-from" className={inputClass} value={shelfFrom}
              onChange={(e) => setShelfFrom(e.target.value.toUpperCase())}
            />
            <span className="text-[12px] text-muted">to</span>
            <input
              className={inputClass} value={shelfTo} aria-label="Last shelf"
              onChange={(e) => setShelfTo(e.target.value.toUpperCase())}
            />
          </div>
        </Field>
        <Field label="Tubs" htmlFor="rack-tub-from" hint="Optional, on each shelf">
          <div className="flex items-center gap-2">
            <input
              id="rack-tub-from" className={inputClass} value={tubFrom} inputMode="numeric"
              placeholder="none"
              onChange={(e) => setTubFrom(e.target.value)}
            />
            <span className="text-[12px] text-muted">to</span>
            <input
              className={inputClass} value={tubTo} inputMode="numeric" placeholder="none"
              aria-label="Last tub"
              onChange={(e) => setTubTo(e.target.value)}
            />
          </div>
        </Field>
        <Field
          label="Compartments"
          htmlFor="rack-sub-from"
          hint="Optional, inside each tub"
        >
          <div className="flex items-center gap-2">
            <input
              id="rack-sub-from" className={inputClass} value={subFrom} placeholder="none"
              onChange={(e) => setSubFrom(e.target.value.toUpperCase())}
            />
            <span className="text-[12px] text-muted">to</span>
            <input
              className={inputClass} value={subTo} placeholder="none"
              aria-label="Last compartment"
              onChange={(e) => setSubTo(e.target.value.toUpperCase())}
            />
          </div>
        </Field>
      </div>

      {/* Said BEFORE the button is pressed. 48 is a row; 4,800 is a typo. */}
      <p className="mt-3 text-[12.5px]">
        This covers <strong>{count.toLocaleString('en-AU')}</strong>{' '}
        {count === 1 ? 'location' : 'locations'}
        {row && `, ${chosen ? '' : ''}starting at `}
        {row && (
          <span className="font-[family-name:var(--font-mono)]">
            {(choices.find((x) => x.id === chosen)?.code ?? '01')}-{row}
            {bayFrom && `-${String(bayFrom).padStart(2, '0')}`}
            {shelfFrom && `-${shelfFrom}`}
            {tubFrom && `-${String(tubFrom).padStart(2, '0')}`}
            {subFrom && `-${subFrom}`}
          </span>
        )}. Locations that are already there stay as they are. Removed ones
        come back with the same code.
      </p>

      {error && <Callout tone="danger" title={error} className="mt-3" />}
      {made != null && (
        <Callout
          tone="ok"
          title={[
            `Added ${made.created}`,
            made.restored > 0 ? `Restored ${made.restored}` : null,
            made.already > 0 ? `${made.already} already there` : null,
          ].filter(Boolean).join('. ') + '.'}
          className="mt-3"
        >
          Print their labels from the list below, then move stock onto them.
        </Callout>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button type="submit" className={buttonClass('primary', 'sm')} disabled={add.isPending}>
          {add.isPending ? 'Adding' : `Add ${count.toLocaleString('en-AU')}`}
        </button>
        <button
          type="button"
          className={buttonClass('secondary', 'sm')}
          onClick={() => { setOpen(false); setError(null); setMade(null); setTitle(null); }}
        >
          Close
        </button>
      </div>
    </form>
  );
}

/**
 * A place that is not on the racking. D019, and spec 007 D-3 and FR-704.
 *
 * The empty state used to say locations "are set up once with Guy", which was
 * true and also the whole problem: there was no route and no form, so a shelf
 * added in September meant asking for a script to be run. The bulk load is
 * still right for day one. This is the one-off. R-191.
 *
 * ── WHY THE WORDS CHANGED ────────────────────────────────────────────────────
 *
 * This form was called "Add a location" and its hints read "BAY-3, STORE,
 * RACK-A1" and "Second rack, top shelf". Every one of those words describes
 * racking, and the form has no Row box, so a location it makes has no row. The
 * rack map is built from rows, so the map cannot draw it. The person sees
 * nothing appear and concludes the button did nothing.
 *
 * The client database holds the proof: a live location `BAY-1`, zone `SHELF-D`,
 * label "Row F, bay 01, shelf D, tub 03, compartment A". Somebody typed a rack
 * position into the only form that looked like it would take one. Then Guy
 * wrote tracker #5: "the Stock Locations Add field doesn't line up with the
 * names of the actual locations them selves, I feel we are missing the Row".
 *
 * The answer is not a Row box here. Two forms that both make rack codes is how
 * one shelf ends up with two codes. The answer is that this form says what it
 * is for, refuses a rack position, and opens the other form.
 */
function AddLocation() {
  const qc = useQueryClient();
  const ui = useContext(LocationsUi);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [zone, setZone] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      call('/api/locations', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code, zone, label }),
      }),
    onSuccess: () => {
      setCode(''); setZone(''); setLabel(''); setError(null); setOpen(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'locations'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  // Said while they type, not after they press Add. A refusal that arrives
  // after the work is a refusal that wasted the work.
  const isRack = looksLikeRackCode(code);

  if (!open) {
    return (
      <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => setOpen(true)}>
        Add a place that is not racking
      </button>
    );
  }

  return (
    <form
      className="w-full rounded-[10px] border border-line bg-panel p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (isRack) { setError(RACK_CODE_REFUSAL); return; }
        add.mutate();
      }}
    >
      <p className="mb-1 text-[14px] font-semibold">Add a place that is not racking</p>
      <p className="mb-3 text-[12.5px] text-muted">
        A packing bench, a cutting table, a delivery cage, a van. For a bay, a
        shelf or a tub on the racking, use <strong>Add a row of racking</strong>,
        which builds the code from the building, row, bay, shelf and tub.
      </p>

      <div className="grid gap-3 sm:grid-cols-[160px_140px_1fr]">
        <Field label="Code" htmlFor="loc-code" hint="PACKING-BENCH, STORE, VAN">
          <input
            id="loc-code" className={inputClass} value={code} required
            aria-invalid={isRack || undefined}
            onChange={(e) => { setCode(e.target.value); setError(null); }}
            placeholder="PACKING-BENCH"
          />
        </Field>
        <Field label="Zone" htmlFor="loc-zone" hint="Where in the unit">
          <input
            id="loc-zone" className={inputClass} value={zone} required
            onChange={(e) => setZone(e.target.value)} placeholder="FLOOR"
          />
        </Field>
        <Field label="Label" htmlFor="loc-label" hint="What somebody standing there would call it">
          <input
            id="loc-label" className={inputClass} value={label} required
            onChange={(e) => setLabel(e.target.value)} placeholder="Packing bench by the door"
          />
        </Field>
      </div>

      {(isRack || error) && (
        <Callout tone="danger" title={isRack ? RACK_CODE_REFUSAL : (error as string)} className="mt-3">
          {isRack && (
            <button
              type="button"
              className={buttonClass('secondary', 'sm') + ' mt-2'}
              onClick={() => { setOpen(false); setError(null); ui.addRack(RACK_FROM_SCRATCH); }}
            >
              Open Add a row of racking
            </button>
          )}
        </Callout>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="submit"
          className={buttonClass('primary', 'sm')}
          disabled={add.isPending || isRack}
        >
          {add.isPending ? 'Adding' : 'Add location'}
        </button>
        <button
          type="button"
          className={buttonClass('secondary', 'sm')}
          onClick={() => { setOpen(false); setError(null); }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * An empty racking form, opened from the refusal above.
 *
 * The building is blank on purpose: the rack form falls back to the first real
 * building, which is the one Guy has. The row is blank because that is the part
 * the person came here to type.
 */
const RACK_FROM_SCRATCH: Omit<RackPreset, 'key'> = {
  siteId: '', row: '',
  bayFrom: '1', bayTo: '12',
  shelfFrom: 'A', shelfTo: 'D',
  tubFrom: '', tubTo: '',
  subFrom: '', subTo: '',
  title: 'Add a row of racking',
};

/**
 * The parts a physical location is found by, in the order you walk to it.
 * A whole row of racking is hundreds of locations, and a list you can only
 * scroll is a list nobody uses to find bay 07.
 */
const PARTS = [
  { key: 'site', label: 'Building' },
  { key: 'row_code', label: 'Row' },
  { key: 'bay', label: 'Bay' },
  { key: 'shelf', label: 'Shelf' },
  { key: 'tub', label: 'Tub' },
  { key: 'sub', label: 'Compartment' },
] as const;

type PartKey = (typeof PARTS)[number]['key'];
type Filter = Partial<Record<PartKey, string>>;

function partOf(l: Location, key: PartKey): string | null {
  if (key === 'site') return l.sites?.code ?? null;
  return l[key] ?? null;
}

function matches(l: Location, f: Filter, except?: PartKey): boolean {
  return PARTS.every(({ key }) =>
    key === except || !f[key] || partOf(l, key) === f[key]);
}

/**
 * One select per part. Each select offers only the values that exist under
 * the parts already chosen, so row B never offers a bay that only row A has.
 * A part that no location uses is not shown at all.
 */
function LocationFilter({
  locations, filter, onChange,
}: {
  locations: Location[];
  filter: Filter;
  onChange: (f: Filter) => void;
}) {
  const shown = PARTS.filter(({ key }) => locations.some((l) => partOf(l, key)));
  if (shown.length === 0) return null;
  const active = Object.values(filter).some(Boolean);

  return (
    <div className="mb-5 flex flex-wrap items-end gap-3 rounded-[10px] border border-line bg-panel p-3">
      {shown.map(({ key, label }) => {
        const values = [...new Set(
          locations
            .filter((l) => matches(l, filter, key))
            .map((l) => partOf(l, key))
            .filter((v): v is string => Boolean(v)),
        )].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
        return (
          <Field key={key} label={label} htmlFor={`loc-f-${key}`}>
            <select
              id={`loc-f-${key}`}
              className={`${inputClass} w-[120px]`}
              value={filter[key] ?? ''}
              onChange={(e) => {
                // A change to one part clears the parts after it, because
                // "bay 07" under row A is not the same place under row B.
                const next: Filter = {};
                for (const p of PARTS) {
                  if (p.key === key) { next[key] = e.target.value || undefined; break; }
                  next[p.key] = filter[p.key];
                }
                onChange(next);
              }}
            >
              <option value="">All</option>
              {values.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
        );
      })}
      {active && (
        <button
          type="button"
          className={buttonClass('secondary', 'sm')}
          onClick={() => onChange({})}
        >
          Clear
        </button>
      )}
    </div>
  );
}

export default function AdminLocationsPage() {
  const { locations } = useSuspenseQuery(adminLocationsQuery).data;
  // Adding racking is inventory.create. Without it the map draws no "+ Add
  // shelf" in a gap and no "Add a shelf" in the panel: a button that the
  // server will refuse is a button that teaches people the screen is broken.
  const { can } = useSuspenseQuery(adminShellQuery).data;
  const mayAddRacking = (can.inventory ?? []).includes('create');

  /**
   * GROUPED BY BUILDING, not by `zone`.
   *
   * `zone` is the old three level idea and is null on every location the
   * racking form makes, so a whole row of racking appeared under a blank
   * heading. That is how somebody decides the form did not work and stops
   * using it.
   *
   * A building groups by its code and name. The two imported Shopify rows have
   * no real building, so they keep their old `zone` heading, which reads
   * SHOPIFY and is the truth about them. Spec 005.
   */
  const groupOf = (l: (typeof locations)[number]) =>
    l.sites?.code ? `${l.sites.code} · ${l.sites.name}` : (l.zone ?? 'Unsorted');

  const [filter, setFilter] = useState<Filter>({});

  /**
   * REMOVED MEANS GONE FROM THE SCREEN. Tracker #4.
   *
   * A location that has ever held stock cannot be deleted: the whole ledger
   * names the place each movement happened, so `deleteLocation` switches it
   * off instead and says so. That part was right. What was wrong is that this
   * screen then drew it exactly like a live one, with no badge and no
   * difference of any kind.
   *
   * So Guy pressed Remove, watched the shelf stay where it was, and wrote
   * "I can do but very slowly" on the tracker. He was not being slow. The
   * screen was telling him the button had not worked.
   *
   * They are hidden rather than dropped, because a count that happened on a
   * shelf that is now gone is still part of the record, and somebody will go
   * looking for it.
   */
  const [showRetired, setShowRetired] = useState(false);
  const [lastRemoved, setLastRemoved] = useState<{ id: string; code: string } | null>(null);
  const [rackPreset, setRackPreset] = useState<RackPreset | null>(null);
  const undo = useRestore();
  // The Undo stays long enough to notice and to reach. Spec 007 FR-706.
  useEffect(() => {
    if (!lastRemoved) return;
    const t = setTimeout(() => setLastRemoved(null), 15_000);
    return () => clearTimeout(t);
  }, [lastRemoved]);
  const ui = {
    removed: (loc: { id: string; code: string }) => setLastRemoved(loc),
    addRack: (p: Omit<RackPreset, 'key'>) => setRackPreset({ ...p, key: Date.now() }),
  };
  const retired = locations.filter((l) => !l.active);
  const shown = showRetired ? locations : locations.filter((l) => l.active);

  // The map is the default once racking exists: it is how the store room is
  // laid out. The list stays for the imported locations, which have no row and
  // so no place on a map, and for filtering by any part of a code.
  const hasRacking = shown.some((l) => l.row_code);
  const [view, setView] = useState<'map' | 'list'>(
    locations.some((l) => l.active && l.row_code) ? 'map' : 'list',
  );
  const unracked = shown.filter((l) => !l.row_code);
  const visible = shown.filter((l) => matches(l, filter));
  const groups = [...new Set(visible.map(groupOf))].sort();
  const counting = locations.filter((l) => l.locked_by_take_id).length;

  return (
    <LocationsUi.Provider value={ui}>
    <div>
      {lastRemoved && (
        <div
          role="status"
          className="fixed bottom-5 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-[10px] bg-ink px-4 py-3 text-[14px] text-panel shadow-lg"
        >
          <span>
            Removed <span className="font-[family-name:var(--font-mono)]">{lastRemoved.code}</span>.
          </span>
          <button
            type="button"
            className="font-semibold underline"
            disabled={undo.isPending}
            onClick={() => undo.mutate(lastRemoved.id, { onSuccess: () => setLastRemoved(null) })}
          >
            {undo.isPending ? 'Restoring' : 'Undo'}
          </button>
        </div>
      )}
      <div className="mb-[14px] flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Locations</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            Every place stock can sit, and what is in it. A count starts here,
            because a count is of a place.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasRacking && (
            <div role="group" aria-label="View" className="flex rounded-[8px] border border-line bg-raised p-[3px]">
              {(['map', 'list'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={view === v}
                  onClick={() => setView(v)}
                  className={
                    'h-8 rounded-[6px] px-3.5 text-[13px] '
                    + (view === v ? 'bg-panel font-semibold text-ink shadow-sm' : 'text-ink-2')
                  }
                >
                  {v === 'map' ? 'Map' : 'List'}
                </button>
              ))}
            </div>
          )}
          <AddRack preset={rackPreset} />
          <AddLocation />
        </div>
      </div>

      {counting > 0 && (
        <div className="mb-5 rounded-[12px] border border-line bg-panel p-4 text-[14px]">
          <strong>{counting} {counting === 1 ? 'shelf is' : 'shelves are'} being
          counted.</strong>{' '}
          Nothing can move off them until the count is approved or cancelled, so
          that the variance measures the shelf rather than how long the count took.
        </div>
      )}

      {retired.length > 0 && (
        <p className="mb-5 text-[13px] text-muted">
          {retired.length} removed {retired.length === 1 ? 'location is' : 'locations are'}{' '}
          {showRetired ? 'shown below.' : 'hidden.'}{' '}
          <button
            type="button"
            className="underline"
            onClick={() => setShowRetired((v) => !v)}
          >
            {showRetired ? 'Hide them' : 'Show them'}
          </button>
        </p>
      )}

      {shown.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center">
          <p className="text-[16px] font-semibold">No locations yet.</p>
          <p className="mx-auto mt-2 max-w-[46ch] text-[13px] text-muted">
            Locations are the shelves, bays and tubs stock is kept in. The layout
            of the unit is loaded in one go at setup. Add one here whenever a new
            shelf appears.
          </p>
          <div className="mt-4 flex justify-center">
            <AddRack />
            <AddLocation />
          </div>
        </div>
      ) : view === 'map' ? (
        <>
          <RackMap locations={shown} onAddRack={mayAddRacking ? ui.addRack : undefined} />
          {/* Locations with no row have no place on a map. The two imported
              Shopify records are where the imported stock still sits, so the
              map view must not hide them. */}
          {unracked.length > 0 && (
            <div className="mt-6">
              {[...new Set(unracked.map(groupOf))].sort().map((group) => (
                <Zone
                  key={group}
                  zone={`${group} · not on a rack`}
                  locations={unracked.filter((l) => groupOf(l) === group)}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <LocationFilter locations={shown} filter={filter} onChange={setFilter} />
          {visible.length !== shown.length && (
            <p className="mb-3 text-[13px] text-muted">
              Showing {visible.length} of {shown.length} locations.
            </p>
          )}
          {visible.length === 0 ? (
            <div className="rounded-[10px] border border-line bg-panel p-6 text-center text-[14px] text-muted">
              No location matches this filter.
            </div>
          ) : (
            groups.map((group) => (
              <Zone
                key={group}
                zone={group}
                locations={visible.filter((l) => groupOf(l) === group)}
              />
            ))
          )}
        </>
      )}
    </div>
    </LocationsUi.Provider>
  );
}
